import '@i18n/i18n';
import { Toaster } from '@neogeniux/uikit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FutureConfig, RouterProvider } from 'react-router-dom';
import { useThemeStore } from './shared/store/theme.store';
import { rootRouter } from './router';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      // 30 s — a query is not immediately stale on remount, so React
      // StrictMode's dev mount→unmount→remount (and normal navigate-away
      // -and-back) no longer fire a duplicate fetch. Explicit
      // invalidateQueries after a mutation still refetches immediately,
      // since invalidation marks the query stale regardless of staleTime.
      staleTime: 30 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
});

export function App() {
  // Bind Sonner's theme prop to our theme store so toasts match the
  // active app palette. The uikit Toaster falls back to next-themes
  // (which we don't wire), so without this override Sonner reads OS
  // preference and toasts stay light while the app is dark.
  const theme = useThemeStore((s) => s.theme);
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        richColors
        expand
        position="top-right"
        closeButton
        theme={theme}
        // Radix Dialog / AlertDialog portals default to z-50 but some
        // legacy modals stamp higher inline z-indexes on their
        // overlay (up to 9999). Pin the toaster viewport above any
        // realistic overlay so error toasts fired from inside a
        // modal land on top instead of behind the shadow.
        // `--sonner-z-index` is Sonner's own CSS variable that its
        // toast wrapper reads; setting it here beats the default
        // z-index without depending on the `style` prop.
        style={
          {
            zIndex: 2147483000,
            ['--sonner-z-index' as string]: '2147483000',
          } as React.CSSProperties
        }
      />
      <RouterProvider
        router={rootRouter}
        future={
          {
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          } as FutureConfig
        }
      />
    </QueryClientProvider>
  );
}

export default App;
